import {
  AutoPayStatus,
  EnrollmentStatus,
  InitialPremiumPaymentStatus,
} from '@zorro/clients';
import { extractStateCodeFromExternalID, isDefined } from '@zorro/common-utils';
import { useMonolithMutation, useMonolithQuery } from '@zorro/web/ui-utils';
import { useCallback, useMemo } from 'react';

import { useAnalytics } from '../Analytics';
import { parseMonthlyAutoPayDateFromOptions } from '../Payments';
import {
  FlagType,
  PaymentCallbacks,
  buildCommunicationMessage,
} from './communication';

type UseEnrollmentCommunicationParams = {
  onboardingPeriodId: string;
  employerId: string;
  employeeId: string;
  enrollmentStatus: EnrollmentStatus;
  isEmployeeMode: boolean;
  benefitId?: string;
  effectiveFrom: string | null;
  planCarrierName?: string | null;
  planExternalId?: string | null;
};

export const useEnrollmentCommunication = ({
  onboardingPeriodId,
  employerId,
  employeeId,
  enrollmentStatus,
  effectiveFrom,
  benefitId,
  planCarrierName,
  planExternalId,
  isEmployeeMode,
}: UseEnrollmentCommunicationParams) => {
  const { sendEvent } = useAnalytics();

  const { tryMutate: updateAorCancellationStatus } = useMonolithMutation({
    method: 'benefitsControllerUpdateAorConfirmedByEmployee',
    shouldReloadPage: false,
    shouldInvalidateQueries: true,
    shouldShowLoadingOverlay: false,
  });

  const { tryMutate: patchMajorMedicalPlan } = useMonolithMutation({
    method: 'majorMedicalControllerPatchMajorMedicalPlan',
    shouldReloadPage: false,
    shouldInvalidateQueries: true,
    shouldShowLoadingOverlay: false,
  });

  const { tryMutate: updateBenefitPaymentStatus } = useMonolithMutation({
    method: 'benefitsControllerUpdatePaymentStatus',
    shouldReloadPage: false,
    shouldInvalidateQueries: true,
    shouldShowLoadingOverlay: false,
  });

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });
  const { data: benefitData } = useMonolithQuery({
    method: 'benefitsControllerGetBenefit',
    params: benefitId && [benefitId],
  });

  const {
    data: communicationConditions,
    isLoading: isLoadingCommunicationConditions,
  } = useMonolithQuery({
    method:
      'enrollmentCommunicationsControllerGetEnrollmentCommunicationConditions',
    params: [onboardingPeriodId, employerId],
  });

  const carrierState = extractStateCodeFromExternalID(planExternalId);

  const { data: carrierInfo } = useMonolithQuery({
    method: 'carrierInfoControllerFindOneByNameAndState',
    params:
      planCarrierName && carrierState
        ? [planCarrierName, carrierState]
        : undefined,
    enabled: isDefined(planCarrierName) && isDefined(carrierState),
  });

  const handleMarkAorCancellationDone = useCallback(async (): Promise<void> => {
    if (!benefitId) {
      return;
    }
    await updateAorCancellationStatus([
      benefitId,
      { isAorConfirmedByEmployee: true },
    ]);
    await sendEvent('aor_cancellation_confirmed', {
      onboarding_period_id: onboardingPeriodId,
    });
  }, [updateAorCancellationStatus, benefitId, onboardingPeriodId, sendEvent]);

  const handleMarkCarrierRequirementsDone =
    useCallback(async (): Promise<void> => {
      if (!benefitId) {
        return;
      }
      await patchMajorMedicalPlan([
        benefitId,
        { isSpecialRequestsConfirmed: true },
      ]);
      await sendEvent('carrier_requirements_confirmed', {
        onboarding_period_id: onboardingPeriodId,
      });
    }, [patchMajorMedicalPlan, benefitId, onboardingPeriodId, sendEvent]);

  const handleMarkPaymentDone = useCallback(
    async (
      type: FlagType.INITIAL_PAYMENT | FlagType.AUTO_PAY,
      selectedDate?: string | null
    ): Promise<void> => {
      if (!benefitId) {
        return;
      }

      const isInitialPayment = type === FlagType.INITIAL_PAYMENT;
      const isAutoPay = type === FlagType.AUTO_PAY;

      await sendEvent(
        isInitialPayment ? 'toggle_payment_details' : 'auto_payment_done',
        { onboardingPeriodId }
      );

      await updateBenefitPaymentStatus([
        benefitId,
        {
          initialPremiumPaymentStatus: isInitialPayment
            ? InitialPremiumPaymentStatus.SET_UP_BY_EMPLOYEE
            : undefined,
          autoPayStatus: isAutoPay
            ? AutoPayStatus.SET_UP_BY_EMPLOYEE
            : undefined,
          ...(isAutoPay && parseMonthlyAutoPayDateFromOptions(selectedDate)),
        },
      ]);
    },
    [
      sendEvent,
      onboardingPeriodId,
      benefitId,
      updateBenefitPaymentStatus,
      benefitData?.initialPremiumPaymentStatus,
      benefitData?.autoPayStatus,
      benefitData?.monthlyAutoPayDate,
    ]
  );

  const paymentCallbacks: PaymentCallbacks = useMemo(
    () => ({
      onMarkInitialPaymentDone: () =>
        handleMarkPaymentDone(FlagType.INITIAL_PAYMENT),
      onMarkAutoPayDone: (date: string | null) =>
        handleMarkPaymentDone(FlagType.AUTO_PAY, date),
    }),
    [handleMarkPaymentDone]
  );

  const communicationMessage = useMemo(() => {
    const sharedProps = {
      status: enrollmentStatus,
      effectiveFrom,
      communicationConditions,
      carrierInfo,
      paymentCallbacks,
      onMarkAorCancellationDone: handleMarkAorCancellationDone,
      onMarkCarrierRequirementsDone: handleMarkCarrierRequirementsDone,
      onboardingPeriodId,
    };
    if (isEmployeeMode) {
      return buildCommunicationMessage({
        ...sharedProps,
        isEmployeeMode: true,
      });
    }
    if (!employee) {
      return undefined;
    }
    return buildCommunicationMessage({
      ...sharedProps,
      isEmployeeMode: false,
      employeeFirstName: employee.firstName,
    });
  }, [
    enrollmentStatus,
    isEmployeeMode,
    employee,
    effectiveFrom,
    communicationConditions,
    carrierInfo,
    paymentCallbacks,
    handleMarkAorCancellationDone,
    handleMarkCarrierRequirementsDone,
    onboardingPeriodId,
  ]);

  return {
    communicationMessage,
    communicationConditions,
    isLoadingCommunicationConditions,
    carrierInfo,
  };
};
